import React, { useRef, useEffect, useState } from 'react';
import Map, { Marker, Source, Layer, NavigationControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapPin, Navigation as NavigationIcon, Package, X, CheckCircle2, AlertCircle, Clock, Image as ImageIcon } from 'lucide-react';
import { computeCollectionAmounts } from '../utils/payments';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

// Coordenadas de la bodega: Carrera 7B, San Cristóbal, Bogotá (Geocodificadas)
const WAREHOUSE_COORDS = {
    lat: 4.570591,
    lon: -74.095938
};

const RouteMapView = ({ route }) => {
    const mapRef = useRef();
    const [viewport, setViewport] = useState({
        latitude: WAREHOUSE_COORDS.lat,
        longitude: WAREHOUSE_COORDS.lon,
        zoom: 11
    });
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [realRoute, setRealRoute] = useState(null); // Ruta real por calles
    // 📍 Última ubicación GPS conocida del mensajero (desde route.locations / breadcrumb trail)
    const messengerLocation = React.useMemo(() => {
        const locs = route?.locations || [];
        if (locs.length === 0) return null;
        const last = locs[locs.length - 1];
        return { lat: parseFloat(last.latitude), lon: parseFloat(last.longitude), timestamp: last.timestamp };
    }, [route?.locations]);

    // Obtener ruta real usando Mapbox Directions API
    useEffect(() => {
        const validOrders = (route?.orders || []).filter(o => o.latitude && o.longitude);
        const validStops = (route?.additionalStops || []).filter(s => s.latitude && s.longitude);

        if (validOrders.length > 0 || validStops.length > 0) {
            // UNIFICAR LISTA (Igual que en MessengerRoutePage)
            const unifiedItems = [
                ...validOrders.map(o => ({ ...o, type: 'order' })),
                ...validStops.map(s => ({ ...s, type: 'stop' }))
            ].sort((a, b) => (parseInt(a.sequenceNumber || a.sequence_number || 0)) - (parseInt(b.sequenceNumber || b.sequence_number || 0)));

            // Construir coordenadas en orden unificado
            const coordinates = [
                [WAREHOUSE_COORDS.lon, WAREHOUSE_COORDS.lat],
                ...unifiedItems.map(item => [item.longitude, item.latitude]),
                [WAREHOUSE_COORDS.lon, WAREHOUSE_COORDS.lat]
            ];

            // Máximo 25 waypoints por request de Mapbox
            const coordsString = coordinates.slice(0, 25).map(c => `${c[0]},${c[1]}`).join(';');

            console.log('🗺️ Mapbox Request Coords:', coordsString);

            // Llamar a Mapbox Directions API
            fetch(`https://api.mapbox.com/directions/v5/mapbox/driving/${coordsString}?geometries=geojson&overview=full&access_token=${MAPBOX_TOKEN}`)
                .then(res => res.json())
                .then(data => {
                    if (data.routes && data.routes[0]) {
                        console.log('✅ Route received from Mapbox');
                        setRealRoute(data.routes[0].geometry);
                    } else {
                        console.warn('⚠️ No routes in Mapbox response:', data);
                    }
                })
                .catch(err => {
                    console.error('Error obteniendo ruta:', err);
                    // Fallback a línea recta si falla
                    setRealRoute({
                        type: 'LineString',
                        coordinates: coordinates
                    });
                });
        }
    }, [route]);

    // Calcular bounds para mostrar todos los puntos
    useEffect(() => {
        const validOrders = (route?.orders || []).filter(o => o.latitude && o.longitude);
        const validStops = (route?.additionalStops || []).filter(s => s.latitude && s.longitude);

        if ((validOrders.length > 0 || validStops.length > 0) && mapRef.current) {
            try {
                const lats = [
                    WAREHOUSE_COORDS.lat,
                    ...validOrders.map(o => o.latitude),
                    ...validOrders.filter(o => o.actualLatitude).map(o => o.actualLatitude),
                    ...validStops.map(s => s.latitude),
                    ...(messengerLocation ? [messengerLocation.lat] : [])
                ];
                const lons = [
                    WAREHOUSE_COORDS.lon,
                    ...validOrders.map(o => o.longitude),
                    ...validOrders.filter(o => o.actualLongitude).map(o => o.actualLongitude),
                    ...validStops.map(s => s.longitude),
                    ...(messengerLocation ? [messengerLocation.lon] : [])
                ];

                const minLat = Math.min(...lats);
                const maxLat = Math.max(...lats);
                const minLon = Math.min(...lons);
                const maxLon = Math.max(...lons);

                // Ajustar el mapa para mostrar todos los puntos
                mapRef.current.fitBounds(
                    [[minLon, minLat], [maxLon, maxLat]],
                    { padding: 20, duration: 1000 }
                );
            } catch (error) {
                console.error('Error ajustando bounds:', error);
            }
        }
    }, [route]);

    if (!route) {
        return (
            <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
                <p className="text-gray-500">No hay datos de ruta disponibles</p>
            </div>
        );
    }

    const validOrders = (route.orders || []).filter(o => o.latitude && o.longitude);

    // Si no hay pedidos geocodificados, mostrar mensaje
    if (validOrders.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-96 bg-gray-50 rounded-lg p-6">
                <AlertCircle className="w-16 h-16 text-yellow-500 mb-4" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Mapa no disponible</h3>
                <p className="text-gray-600 text-center max-w-md mb-4">
                    Los pedidos de esta ruta no tienen coordenadas geocodificadas.
                    Usa el botón "Geocodificar" en la parte superior para intentar localizar las direcciones.
                </p>
                <div className="mt-2 text-sm text-gray-500 mb-4">
                    Total de pedidos: {route.orders?.length || 0}
                </div>
            </div>
        );
    }

    // UNIFICAR Marcadores y Ruta (Igual que en la lista)
    const validStops = (route.additionalStops || []).filter(s => s.latitude && s.longitude);
    const unifiedItems = [
        ...validOrders.map(o => ({ ...o, type: 'order' })),
        ...validStops.map(s => ({ ...s, type: 'stop' }))
    ].sort((a, b) => (parseInt(a.sequenceNumber || a.sequence_number || 0)) - (parseInt(b.sequenceNumber || b.sequence_number || 0)));

    // Usar la ruta real si está disponible, sino fallback a líneas rectas
    const routeLineGeoJSON = realRoute ? {
        type: 'Feature',
        geometry: realRoute
    } : {
        type: 'Feature',
        geometry: {
            type: 'LineString',
            coordinates: [
                [WAREHOUSE_COORDS.lon, WAREHOUSE_COORDS.lat],
                ...unifiedItems.map(item => [item.longitude, item.latitude]),
                [WAREHOUSE_COORDS.lon, WAREHOUSE_COORDS.lat]
            ]
        }
    };

    // Líneas de diferencia entre domicilio y entrega real
    const deltaLinesGeoJSON = {
        type: 'FeatureCollection',
        features: validOrders
            .filter(o => o.actualLatitude && o.actualLongitude)
            .map(o => ({
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: [
                        [o.longitude, o.latitude],
                        [o.actualLongitude, o.actualLatitude]
                    ]
                }
            }))
    };

    // Capa de migas de pan (breadcrumb)
    const breadcrumbGeoJSON = {
        type: 'Feature',
        geometry: {
            type: 'LineString',
            coordinates: (route.locations || []).map(l => [l.longitude, l.latitude])
        }
    };

    const lineLayer = {
        id: 'route-line',
        type: 'line',
        paint: {
            'line-color': '#3B82F6',
            'line-width': 4,
            'line-opacity': 0.8
        }
    };

    const deltaLineLayer = {
        id: 'delta-line',
        type: 'line',
        paint: {
            'line-color': '#EF4444',
            'line-width': 2,
            'line-dasharray': [2, 2],
            'line-opacity': 0.6
        }
    };

    const breadcrumbLayer = {
        id: 'breadcrumb-trail',
        type: 'line',
        paint: {
            'line-color': '#9CA3AF', // Gris
            'line-width': 2,
            'line-opacity': 0.6
        }
    };

    return (
        <div className="relative h-[75vh] min-h-[500px] w-full rounded-lg overflow-hidden shadow-lg">
            <Map
                ref={mapRef}
                {...viewport}
                onMove={evt => setViewport(evt.viewState)}
                mapStyle="mapbox://styles/mapbox/streets-v12"
                mapboxAccessToken={MAPBOX_TOKEN}
                style={{ width: '100%', height: '100%' }}
            >
                <NavigationControl position="top-right" />

                {/* Migas de pan */}
                <Source id="breadcrumb" type="geojson" data={breadcrumbGeoJSON}>
                    <Layer {...breadcrumbLayer} />
                </Source>

                {/* Línea de ruta */}
                <Source id="route" type="geojson" data={routeLineGeoJSON}>
                    <Layer {...lineLayer} />
                </Source>

                {/* Líneas de diferencia GPS */}
                <Source id="delta" type="geojson" data={deltaLinesGeoJSON}>
                    <Layer {...deltaLineLayer} />
                </Source>

                {/* Marcador de bodega (origen) */}
                <Marker
                    latitude={WAREHOUSE_COORDS.lat}
                    longitude={WAREHOUSE_COORDS.lon}
                    anchor="bottom"
                >
                    <div className="relative">
                        <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center shadow-lg border-4 border-white">
                            <NavigationIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-white px-2 py-1 rounded shadow-md text-xs font-bold">
                            Bodega
                        </div>
                    </div>
                </Marker>

                {/* 📍 Marcador de ubicación actual del mensajero */}
                {messengerLocation && (
                    <Marker
                        latitude={messengerLocation.lat}
                        longitude={messengerLocation.lon}
                        anchor="center"
                    >
                        <div className="relative flex items-center justify-center">
                            {/* Anillo de pulso animado */}
                            <div className="absolute w-12 h-12 bg-blue-400/30 rounded-full animate-ping" />
                            <div className="absolute w-8 h-8 bg-blue-400/20 rounded-full" />
                            {/* Punto central */}
                            <div className="relative w-4 h-4 bg-blue-600 rounded-full border-3 border-white shadow-lg z-10" style={{ borderWidth: '3px' }} />
                            {/* Etiqueta */}
                            <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap bg-blue-600 px-2 py-0.5 rounded-full shadow-md text-[10px] font-bold text-white">
                                📍 Mensajero
                            </div>
                        </div>
                    </Marker>
                )}

                {/* Marcadores Unificados */}
                {unifiedItems.map((item, index) => {
                    const displaySequence = index + 1;
                    const isOrder = item.type === 'order';

                    if (isOrder) {
                        const isDelivered = item.deliveryStatus === 'entregado' || item.deliveryStatus === 'delivered' || item.messenger_status === 'delivered';
                        const isFailed = item.deliveryStatus === 'failed' || item.messenger_status === 'failed';

                        return (
                            <React.Fragment key={item.id}>
                                <Marker
                                    latitude={item.latitude}
                                    longitude={item.longitude}
                                    anchor="bottom"
                                    onClick={() => setSelectedOrder(item)}
                                >
                                    <div className="relative cursor-pointer group">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg border-4 border-white transition-transform group-hover:scale-110 ${isDelivered ? 'bg-green-500' :
                                            isFailed ? 'bg-red-500' : 'bg-blue-600'
                                            }`}>
                                            {isDelivered ? (
                                                <CheckCircle2 className="w-6 h-6 text-white" />
                                            ) : isFailed ? (
                                                <AlertCircle className="w-6 h-6 text-white" />
                                            ) : (
                                                <span className="text-white font-bold text-sm">
                                                    {displaySequence}
                                                </span>
                                            )}
                                        </div>

                                        {/* Status Badge (if delivered/failed) */}
                                        {(isDelivered || isFailed) && (
                                            <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-black text-white shadow-sm ${isDelivered ? 'bg-green-600' : 'bg-red-600'
                                                }`}>
                                                {displaySequence}
                                            </div>
                                        )}

                                        {/* Tooltip on hover */}
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50">
                                            <div className="bg-white rounded-lg shadow-xl p-3 min-w-[200px] border border-gray-200">
                                                <div className="text-xs font-bold text-gray-900 mb-1">
                                                    #{item.orderNumber}
                                                </div>
                                                <div className="text-xs text-gray-700 mb-1">
                                                    {item.customerName}
                                                </div>
                                                <div className="text-xs text-gray-500 flex items-start gap-1">
                                                    <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                                    <span className="line-clamp-2">{item.deliveryAddress}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Marker>

                                {/* Marcador de entrega REAL (GPS) */}
                                {item.actualLatitude && item.actualLongitude && (
                                    <Marker
                                        latitude={item.actualLatitude}
                                        longitude={item.actualLongitude}
                                        anchor="center"
                                    >
                                        <div className="relative group">
                                            <div className="w-4 h-4 bg-red-600 rounded-full border-2 border-white shadow-md flex items-center justify-center">
                                                <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                                            </div>
                                        </div>
                                    </Marker>
                                )}
                            </React.Fragment>
                        );
                    } else {
                        const isCompleted = item.status === 'completed';
                        return (
                            <Marker
                                key={`stop-${item.id}`}
                                latitude={item.latitude}
                                longitude={item.longitude}
                                anchor="bottom"
                                onClick={() => setSelectedOrder({ ...item, isAdditionalStop: true })}
                            >
                                <div className="relative cursor-pointer group">
                                    <div className={`p-2 border-2 shadow-xl border-white transition-all group-hover:scale-110 transform rotate-45 ${isCompleted ? 'bg-emerald-600' : 'bg-purple-700'
                                        }`}>
                                        <div className="-rotate-45 flex items-center justify-center">
                                            {isCompleted ? (
                                                <CheckCircle2 className="w-4 h-4 text-white" />
                                            ) : (
                                                <NavigationIcon className="w-4 h-4 text-white fill-current" />
                                            )}
                                        </div>
                                    </div>

                                    {/* Etiqueta flotante mejorada */}
                                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-black/80 text-white px-2 py-0.5 rounded text-[10px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none font-bold">
                                        EXTRA: {item.description || 'Parada'}
                                    </div>

                                    {/* Status Badge (if completed) */}
                                    {isCompleted && (
                                        <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-black text-white shadow-sm bg-green-700">
                                            {displaySequence}
                                        </div>
                                    )}

                                    {/* Tooltip on hover */}
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50">
                                        <div className="bg-purple-700 rounded-lg shadow-xl p-3 min-w-[200px] border border-purple-500">
                                            <div className="text-xs font-bold text-purple-100 mb-1">
                                                PARADA ADICIONAL #{displaySequence}
                                            </div>
                                            <div className="text-xs text-white mb-1 capitalize font-semibold">
                                                {item.stopType === 'pickup' ? 'Recogida' :
                                                    item.stopType === 'delivery' ? 'Entrega' :
                                                        item.stopType === 'errand' ? 'Diligencia' : 'Otra'}
                                            </div>
                                            <div className="text-xs text-purple-100">
                                                {item.description || item.address}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Marker>
                        );
                    }
                })}
            </Map>

            {/* Popup de pedido/parada seleccionada */}
            {
                selectedOrder && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-lg shadow-xl p-4 max-w-sm w-full mx-4">
                        <button
                            onClick={() => setSelectedOrder(null)}
                            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                        >
                            ✕
                        </button>

                        {selectedOrder.isAdditionalStop ? (
                            /* Popup para parada adicional */
                            <>
                                <div className="mb-2">
                                    <span className="inline-block px-2 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-full mb-2">
                                        PARADA ADICIONAL
                                    </span>
                                    <h4 className="font-bold text-gray-900 capitalize">
                                        {selectedOrder.stopType === 'pickup' ? '📦 Recogida' :
                                            selectedOrder.stopType === 'delivery' ? '🚚 Entrega' :
                                                selectedOrder.stopType === 'errand' ? '📝 Diligencia' : '📍 Otra'}
                                    </h4>
                                </div>
                                <div className="space-y-2 text-sm">
                                    {selectedOrder.description && (
                                        <div>
                                            <span className="text-gray-600">Descripción:</span>
                                            <p className="font-semibold">{selectedOrder.description}</p>
                                        </div>
                                    )}
                                    <div>
                                        <span className="text-gray-600 flex items-start gap-1">
                                            <MapPin className="w-4 h-4 mt-0.5" />
                                            Dirección:
                                        </span>
                                        <a
                                            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selectedOrder.address + ', Colombia')}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-semibold text-purple-600 hover:text-purple-700 underline block"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {selectedOrder.address}
                                        </a>
                                    </div>
                                    {selectedOrder.contactName && (
                                        <div>
                                            <span className="text-gray-600">Contacto:</span>
                                            <p className="font-semibold">{selectedOrder.contactName}</p>
                                            {selectedOrder.contactPhone && (
                                                <a href={`tel:${selectedOrder.contactPhone}`} className="text-purple-600 font-medium">
                                                    {selectedOrder.contactPhone}
                                                </a>
                                            )}
                                        </div>
                                    )}
                                    {selectedOrder.paymentAmount > 0 && (
                                        <div className="pt-2 border-t border-gray-200">
                                            <span className="text-gray-600">Cobrar:</span>
                                            <p className="font-bold text-purple-600">
                                                ${parseFloat(selectedOrder.paymentAmount).toLocaleString('es-CO')}
                                            </p>
                                        </div>
                                    )}
                                    {selectedOrder.notes && (
                                        <div className="pt-2 border-t border-gray-200">
                                            <span className="text-gray-600">Notas:</span>
                                            <p className="text-sm text-gray-700">{selectedOrder.notes}</p>
                                        </div>
                                    )}
                                </div>
                                <a
                                    href={`https://www.google.com/maps/dir/?api=1&destination=${selectedOrder.latitude},${selectedOrder.longitude}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-3 w-full bg-purple-600 text-white py-3 rounded-lg font-bold hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 shadow-lg"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <NavigationIcon className="w-5 h-5" />
                                    IR
                                </a>
                                <button
                                    onClick={() => setSelectedOrder(null)}
                                    className="mt-2 w-full bg-gray-100 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                                >
                                    <X className="w-4 h-4" />
                                    Cerrar
                                </button>
                            </>
                        ) : (
                            /* Popup para pedido normal */
                            <>
                                <div className="mb-2">
                                    <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full mb-2">
                                        Parada #{selectedOrder.sequenceNumber}
                                    </span>
                                    <h4 className="font-bold text-gray-900">#{selectedOrder.orderNumber}</h4>
                                </div>
                                <div className="space-y-2 text-sm">
                                    <div>
                                        <span className="text-gray-600">Cliente:</span>
                                        <p className="font-semibold">{selectedOrder.customerName}</p>
                                    </div>

                                    {/* Dirección con link a Google Maps */}
                                    <div>
                                        <span className="text-gray-600 flex items-start gap-1">
                                            <MapPin className="w-4 h-4 mt-0.5" />
                                            Dirección:
                                        </span>
                                        <a
                                            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selectedOrder.deliveryAddress + ', Colombia')}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-semibold text-blue-600 hover:text-blue-700 underline block"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {selectedOrder.deliveryAddress}
                                        </a>
                                    </div>

                                    {selectedOrder.distance && (
                                        <div>
                                            <span className="text-gray-600">Distancia desde bodega:</span>
                                            <p className="font-semibold text-blue-600">
                                                {parseFloat(selectedOrder.distance).toFixed(1)} km
                                            </p>
                                        </div>
                                    )}
                                    {selectedOrder.deliveredAt && (
                                        <div className="flex items-center gap-2 py-1.5 px-3 bg-green-50 rounded-lg border border-green-100 mb-2">
                                            <Clock className="w-4 h-4 text-green-600" />
                                            <div className="text-xs">
                                                <span className="text-green-800 font-bold">Entregado: </span>
                                                <span className="text-green-700">
                                                    {new Date(selectedOrder.deliveredAt).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    {selectedOrder.delivery_evidence_filename && (
                                        <div className="mb-3">
                                            <p className="text-xs text-gray-500 mb-1 font-bold flex items-center gap-1">
                                                <ImageIcon className="w-3 h-3" /> EVIDENCIA DE ENTREGA:
                                            </p>
                                            <img
                                                src={`/uploads/evidence/photo/${selectedOrder.delivery_evidence_filename}`}
                                                alt="Evidencia"
                                                className="w-full h-32 object-cover rounded-lg border border-gray-200 shadow-sm"
                                                onError={(e) => {
                                                    e.target.src = 'https://via.placeholder.com/300x150?text=Error+al+cargar+foto';
                                                }}
                                            />
                                        </div>
                                    )}
                                    {(() => {
                                        const { totalDue } = computeCollectionAmounts(selectedOrder);
                                        if (totalDue > 0) {
                                            return (
                                                <div className="pt-2 border-t border-gray-200">
                                                    <span className="text-gray-600">Cobrar:</span>
                                                    <p className="font-bold text-red-600">
                                                        ${totalDue.toLocaleString('es-CO')}
                                                    </p>
                                                </div>
                                            );
                                        } else {
                                            return (
                                                <div className="pt-2 border-t border-gray-200 flex items-center gap-2 text-green-600">
                                                    <CheckCircle2 className="w-4 h-4" />
                                                    <span className="font-bold">Ok pago / Sin saldo</span>
                                                </div>
                                            );
                                        }
                                    })()}

                                    {/* Botón IR - Navegar en Google Maps */}
                                    <a
                                        href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selectedOrder.deliveryAddress + ', Colombia')}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-3 w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 shadow-lg"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <NavigationIcon className="w-5 h-5" />
                                        IR
                                    </a>

                                    <button
                                        onClick={() => setSelectedOrder(null)}
                                        className="mt-2 w-full bg-red-100 text-red-700 py-2 rounded-lg font-semibold hover:bg-red-200 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <X className="w-4 h-4" />
                                        Cerrar
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )
            }
        </div >
    );
};

export default RouteMapView;
